import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Grid,
  Box,
  Switch,
  Button,
  CircularProgress,
} from '@mui/material';
import Iconify from 'src/components/iconify/Iconify';
import Services from 'src/services/Services';

const SERVICE_ITEMS = [
  { key: 'dhl', label: 'DHL Kargo Hizmetleri' },
  { key: 'fitting_room', label: 'Deneme Kabini' },
  { key: 'storage_cloakroom.status', label: 'Emanet & Vestiyer' },
  { key: 'storage_cloakroom.is_free', label: 'Emanet & Vestiyer Ücretsiz', dependsOn: 'storage_cloakroom.status' },
  { key: 'handsfree', label: 'Hands Free' },
  { key: 'pet_car', label: 'Pet Arabası' },
  { key: 'kid_car', label: 'Çocuk Arabası' },
  { key: 'gift', label: 'Hediye Paketleme' },
  { key: 'locker', label: 'Emanet Dolabı' },
  { key: 'aras_kargo', label: 'Aras Kargo Hizmetleri', partnerKey: 'aras' },
  { key: 'trendyol', label: 'Trendyol Click & Collect', partnerKey: 'trendyol' },
  { key: 'hepsiburada', label: 'Hepsiburada Click & Collect', partnerKey: 'hepsiburada' },
  { key: 'hepsijet', label: 'HepsiJet', partnerKey: 'hepsijet' },
  { key: 'n11', label: 'N11 Click & Collect' },
  { key: 'global_blue_tax_free', label: 'Global Blue Tax Free' },
  { key: 'tax_free_zone', label: 'Tax Free Zone Tax Free' },
  { key: 'planet_tax_free', label: 'Planet Tax Free' },
  { key: 'joy_refund_tax_free', label: 'Joy Refund Tax Free' },
];

const PARTNER_STATUS_MAP = {
  trendyol: { activeKey: 'isActiveOnTrendyol', fallbackKey: 'isTrendyol' },
  hepsiburada: { activeKey: 'isActiveOnHepsiburada', fallbackKey: 'isHepsiburada' },
  hepsijet: { activeKey: 'isActiveOnHepsijet', fallbackKey: 'isHepsijet' },
  kargoist: { activeKey: 'isActiveOnKargoist', fallbackKey: 'isKargoist' },
  amazon: { activeKey: 'isActiveOnAmazon', fallbackKey: 'isAmazon' },
  suratKargo: { activeKey: 'isActiveOnSuratKargo', fallbackKey: 'isSuratKargo' },
  aras: { activeKey: 'isArasKargo' },
};

const PARTNER_KEYS = Object.keys(PARTNER_STATUS_MAP);

export const isFlagOn = (value) => value === '1' || value === 1 || value === true;

export const buildEmptyServiceState = () => {
  const emptyState = {};
  SERVICE_ITEMS.forEach((item) => {
    const path = item.key.split('.');
    let cursor = emptyState;
    path.forEach((segment, index) => {
      if (index === path.length - 1) {
        cursor[segment] = false;
      } else {
        if (!cursor[segment] || typeof cursor[segment] !== 'object') {
          cursor[segment] = {};
        }
        cursor = cursor[segment];
      }
    });
  });
  return emptyState;
};

const getValueByPath = (obj, path) => path.reduce((acc, key) => (acc ? acc[key] : undefined), obj);

const setValueByPath = (obj, path, value) => {
  let cursor = obj;
  path.forEach((segment, index) => {
    if (index === path.length - 1) {
      cursor[segment] = value;
    } else {
      if (!cursor[segment] || typeof cursor[segment] !== 'object') {
        cursor[segment] = {};
      }
      cursor = cursor[segment];
    }
  });
};

const LEGACY_KEY_MAP = {
  denemeKabini: 'fitting_room',
  emanetVestiyer: 'storage_cloakroom.status',
  emanetDolabi: 'locker',
  handsFree: 'handsfree',
  petArabasi: 'pet_car',
  cocukArabasi: 'kid_car',
  hediyePaketleme: 'gift',
  aras: 'aras_kargo',
  globalBlue: 'global_blue_tax_free',
  taxFreeZone: 'tax_free_zone',
  planetTaxFree: 'planet_tax_free',
  joyRefund: 'joy_refund_tax_free',
};

function getPartnerState(point, serviceKey) {
  const cfg = PARTNER_STATUS_MAP[serviceKey];
  if (!cfg || !point) {
    return undefined;
  }

  const activeValue = point[cfg.activeKey];
  if (activeValue !== undefined && activeValue !== null) {
    return isFlagOn(activeValue);
  }

  if (cfg.fallbackKey) {
    const fallbackValue = point[cfg.fallbackKey];
    if (fallbackValue !== undefined && fallbackValue !== null) {
      return isFlagOn(fallbackValue);
    }
  }

  return undefined;
}

export function mapPointServicesToState(pointServices) {
  const mappedState = buildEmptyServiceState();

  if (!pointServices) {
    return mappedState;
  }

  let parsedValue = pointServices;

  if (typeof pointServices === 'string') {
    try {
      parsedValue = JSON.parse(pointServices);
    } catch (error) {
      parsedValue = pointServices
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  if (Array.isArray(parsedValue)) {
    parsedValue.forEach((serviceKey) => {
      const mappedKey = LEGACY_KEY_MAP[serviceKey] || serviceKey;
      const path = mappedKey.split('.');
      if (SERVICE_ITEMS.some((item) => item.key === mappedKey)) {
        setValueByPath(mappedState, path, true);
      }
    });
    return mappedState;
  }

  if (parsedValue && typeof parsedValue === 'object') {
    SERVICE_ITEMS.forEach((item) => {
      const path = item.key.split('.');
      const value = getValueByPath(parsedValue, path);
      if (value !== undefined) {
        setValueByPath(mappedState, path, isFlagOn(value));
      }
    });

    if (parsedValue.storage_cloakroom && typeof parsedValue.storage_cloakroom !== 'object') {
      setValueByPath(mappedState, ['storage_cloakroom', 'status'], isFlagOn(parsedValue.storage_cloakroom));
    }
  }

  return mappedState;
}

export default function PointServicePermissionsModal({
  open,
  point,
  onClose,
  onSave,
  isSaving = false,
}) {
  const [serviceStates, setServiceStates] = useState(buildEmptyServiceState());
  const [serviceOverrides, setServiceOverrides] = useState(null);
  const [lockedServiceKeys, setLockedServiceKeys] = useState([]);
  const [isVehicleStatusLoading, setIsVehicleStatusLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setLockedServiceKeys(['pet_car', 'kid_car', 'gift']);
    setServiceOverrides({ pet_car: false, kid_car: false, gift: false });
    setIsVehicleStatusLoading(true);

    const mallID =
      point?.mallID ??
      point?.details?.mallID ??
      point?.details?.id ??
      point?.mall?.id;
    let token = '';
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      token = storedUser?.token || '';
    } catch (err) {
      token = '';
    }

    console.log('PointServicePermissionsModal GetMall request', {
      mallID,
      hasToken: Boolean(token),
      point,
    });

    if (!mallID || !token) {
      console.log('PointServicePermissionsModal GetMall skipped', {
        mallID,
        hasToken: Boolean(token),
      });
      setIsVehicleStatusLoading(false);
      return;
    }

    Services.GetMall(token, mallID)
      .then((response) => {
        console.log('PointServicePermissionsModal GetMall response', response?.data);
        if (!response?.data?.status) {
          return;
        }
        const inventoryWithPrice = response?.data?.result?.vehicleInventoryWithPrice || {};
        const petStatus = isFlagOn(inventoryWithPrice['pet-car']?.status);
        const kidStatus = isFlagOn(inventoryWithPrice['kid-car']?.status);
        const giftStatus = isFlagOn(inventoryWithPrice.gift?.status);
        setServiceOverrides({ pet_car: petStatus, kid_car: kidStatus, gift: giftStatus });
      })
      .catch((err) => {
        console.log('PointServicePermissionsModal GetMall error', err);
      })
      .finally(() => setIsVehicleStatusLoading(false));
  }, [open, point]);

  useEffect(() => {
    if (!open) {
      setServiceOverrides(null);
      setLockedServiceKeys([]);
      setIsVehicleStatusLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const baseState = mapPointServicesToState(point?.pointServices);
    const nextState = {
      ...baseState,
      storage_cloakroom: { ...(baseState?.storage_cloakroom || {}) },
    };

    SERVICE_ITEMS.forEach((item) => {
      if (!item.partnerKey || !PARTNER_KEYS.includes(item.partnerKey)) {
        return;
      }
      const partnerState = getPartnerState(point, item.partnerKey);
      if (partnerState !== undefined) {
        setValueByPath(nextState, item.key.split('.'), partnerState);
      }
    });

    if (serviceOverrides && typeof serviceOverrides === 'object') {
      Object.entries(serviceOverrides).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return;
        }
        setValueByPath(nextState, key.split('.'), isFlagOn(value));
      });
    }

    setServiceStates(nextState);
  }, [open, point, serviceOverrides]);

  const handleToggleService = (key, checked) => {
    const path = key.split('.');
    setServiceStates((prev) => {
      const nextState = {
        ...prev,
        storage_cloakroom: { ...(prev?.storage_cloakroom || {}) },
      };
      setValueByPath(nextState, path, checked);
      if (key === 'storage_cloakroom.status' && !checked) {
        setValueByPath(nextState, ['storage_cloakroom', 'is_free'], false);
      }
      return nextState;
    });
  };

  const handleSave = () => {
    const payload = buildEmptyServiceState();
    SERVICE_ITEMS.forEach((item) => {
      const path = item.key.split('.');
      const value = getValueByPath(serviceStates, path);
      setValueByPath(payload, path, isFlagOn(value));
    });
    onSave(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: { borderRadius: '12px' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
        <Typography variant="h6" fontWeight="bold">Nokta Hizmet Yetkileri</Typography>
        <IconButton onClick={onClose}>
          <Iconify icon="mdi:close" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderTop: 'none' }}>
        <Grid container spacing={2}>
          {SERVICE_ITEMS.map((item) => {
            const currentValue = getValueByPath(serviceStates, item.key.split('.'));
            const isPartnerControlled = item.partnerKey && PARTNER_KEYS.includes(item.partnerKey);
            const dependsOnPath = item.dependsOn ? item.dependsOn.split('.') : null;
            const isDependencyOff = dependsOnPath ? !getValueByPath(serviceStates, dependsOnPath) : false;
            const isExternallyLocked = lockedServiceKeys.includes(item.key);
            const isDisabled = isPartnerControlled || isDependencyOff || isExternallyLocked;
            const isManagedByEpService = item.key === 'pet_car' || item.key === 'kid_car' || item.key === 'gift';
            const showLoader = isManagedByEpService && isVehicleStatusLoading;

            return (
            <Grid item xs={12} sm={6} key={item.key}>
              <Box
                sx={{
                  border: '1px solid #EAECF0',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="body2" color="textPrimary">
                  {item.label}
                </Typography>
                {showLoader ? (
                  <CircularProgress size={18} />
                ) : (
                  <Switch
                    checked={Boolean(currentValue)}
                    onChange={(e) => handleToggleService(item.key, e.target.checked)}
                    color="error"
                    disabled={isDisabled}
                  />
                )}
              </Box>
            </Grid>
          )})}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: '24px' }}>
        <Button
          onClick={onClose}
          disabled={isSaving}
          sx={{
            backgroundColor: '#F3F4F6',
            color: '#344054',
            padding: '8px 20px',
            '&:hover': { backgroundColor: '#E5E7EB' },
          }}
        >
          Vazgeç
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          sx={{
            backgroundColor: '#DC423C',
            padding: '8px 20px',
            '&:hover': { backgroundColor: '#B53228' },
          }}
        >
          Değişiklikleri Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PointServicePermissionsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  point: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};
